import { useState, FormEvent, ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Address } from '../types/'
import { api } from '../services/api';
import './SignUpPage.css';

interface FormData {
  firstName: string;
  lastName: string;
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  username: string;
  password: string;
  address: Address;
}

export const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    name: '',
    jobTitle: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: ''
    }
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [checkingUsername, setCheckingUsername] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1] as keyof Address;
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Validate username on change
      if (name === 'username') {
        setUsernameError(null);
        if (value.length >= 3) {
          validateUsername(value);
        }
      }
    }
  };

  const validateUsername = async (username: string) => {
    if (!username || username.length < 3) {
      return;
    }

    setCheckingUsername(true);
    setUsernameError(null);

    try {
      const users = await api.getUsers();
      
      // Check if username exists (case-insensitive)
      const usernameExists = users.some(
        (user: any) => user.id?.toLowerCase() === username.toLowerCase()
      );

      if (usernameExists) {
        setUsernameError('This username is already taken');
      }
    } catch (err) {
      console.error('Username validation error:', err);
      // Don't block submission on validation error
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Prevent submission if username is invalid
    if (usernameError) {
      setError('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newUser = await api.registerUser({
        ...formData,
        ...(redirectUrl ? { redirectUrl } : {}),
      });

      if (!newUser) {
         console.log('User registration failed');
      }

      setSuccess(true);
      // Optionally redirect after success
      // setTimeout(() => window.location.href = '/login', 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  if (success) {
    return (
      <div className="login-container">
        <div className="success-card">
          <div className="success-icon">✓</div>
          <h2 className="success-title">Registration Successful!</h2>
          <p className="success-message">
            Thank you for signing up. We've received your registration. You should receive an email
            to confirm your email address and complete the registration process.
          </p>
          <a href={redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'} className="btn-home">
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
      <div className="signup-card">
        {/* Header */}
        <div className="login-header">
          <h1 className="login-title">Create Your Account</h1>
          <p className="login-subtitle">Join QBD Connect and start integrating today</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="signup-form">
          {error && (
            <div className="alert-error">
              {error}
            </div>
          )}

          <div className="form-row">
            {/* First Name */}
            <div className="form-group">
              <label className="form-label">
                First Name <span className="required">*</span>
              </label>
              <input type="text" name="firstName" value={formData.firstName}
                onChange={handleChange} required className="form-input" placeholder="John"/>
            </div>

            {/* Last Name */}
            <div className="form-group">
              <label className="form-label">
                Last Name <span className="required">*</span>
              </label>
              <input type="text" name="lastName" value={formData.lastName}
                onChange={handleChange} required className="form-input" placeholder="Smith"/>
            </div>
          </div>

          <div className="form-row">
            {/* Name (Alias) */}
            <div className="form-group">
              <label className="form-label">Name (Alias)</label>
              <input type="text" name="name" value={formData.name}
                onChange={handleChange} className="form-input" placeholder="JSmith"/>
            </div>

            {/* Job Title */}
            <div className="form-group">
              <label className="form-label">Job Title</label>
              <input type="text" name="jobTitle" value={formData.jobTitle}
                onChange={handleChange} className="form-input" placeholder="Accountant"/>
            </div>
          </div>

          <div className="form-row">
            {/* Email */}
            <div className="form-group">
              <label className="form-label">
                Email <span className="required">*</span>
              </label>
              <input type="email" name="email" value={formData.email}
                onChange={handleChange} required className="form-input" placeholder="john@example.com"/>
            </div>

            {/* Phone */}
            <div className="form-group">
              <label className="form-label">
                Phone <span className="required">*</span>
              </label>
              <input type="tel" name="phone" value={formData.phone}
                onChange={handleChange} required className="form-input" placeholder="(555) 123-4567"/>
            </div>
          </div>

          {/* Username */}
          <div className="form-group">
            <label className="form-label">
              Username <span className="required">*</span>
              {checkingUsername && <span className="checking-text"> (checking...)</span>}
            </label>
            <input type="text" name="username" value={formData.username}
              onChange={handleChange} required
              className={`form-input ${usernameError ? 'input-error' : ''} ${formData.username && !usernameError && !checkingUsername ? 'input-success' : ''}`}
              placeholder="Choose a unique username" minLength={3}/>
            {usernameError && (
              <span className="field-error">{usernameError}</span>
            )}
            {formData.username && !usernameError && !checkingUsername && formData.username.length >= 3 && (
              <span className="field-success">✓ Username available</span>
            )}
          </div>

          <div className="form-row">
            {/* Password */}
            <div className="form-group">
              <label className="form-label">
                Password <span className="required">*</span>
              </label>
              <input type="password" name="password" value={formData.password}
                onChange={handleChange} required className="form-input" placeholder="Password"/>
            </div>
          </div>

          {/* Address Section */}
          <div className="address-section">
            <h3 className="section-title">Address Information</h3>

            {/* Street 1 */}
            <div className="form-group">
              <label className="form-label">
                Street Address <span className="required">*</span>
              </label>
              <input type="text" name="address.street1" value={formData.address.street1}
                onChange={handleChange} required className="form-input" placeholder="123 Main Street"/>
            </div>

            {/* Street 2 */}
            <div className="form-group">
              <label className="form-label">Street Address 2</label>
              <input type="text" name="address.street2" value={formData.address.street2}
                onChange={handleChange} className="form-input" placeholder="Apt, Suite, Unit, etc. (optional)"/>
            </div>

            <div className="address-row">
              {/* City */}
              <div className="form-group form-group-city">
                <label className="form-label">
                  City <span className="required">*</span>
                </label>
                <input type="text" name="address.city" value={formData.address.city}
                  onChange={handleChange} required className="form-input" placeholder="Akron"/>
              </div>

              {/* State */}
              <div className="form-group form-group-state">
                <label className="form-label">
                  State <span className="required">*</span>
                </label>
                <select name="address.state" value={formData.address.state} onChange={handleChange}
                    required className="form-input">
                  <option value="">Select</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Postal Code */}
              <div className="form-group form-group-postal">
                <label className="form-label">
                  Postal Code <span className="required">*</span>
                </label>
                <input type="text" name="address.postalCode" value={formData.address.postalCode}
                  onChange={handleChange} required className="form-input" placeholder="12345"
                  pattern="[0-9]{5}" title="Please enter a 5-digit ZIP code"/>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={loading || !!usernameError || checkingUsername}
              className={`btn-submit ${loading || checkingUsername ? 'btn-loading' : ''}`}>
            {loading ? 'Creating Account...' : checkingUsername ? 'Validating...' : 'Create Account'}
          </button>

          <p className="login-footer">
            Already have an account?{' '}<a href={redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'} className="login-link">Sign in</a>
          </p>
        </form>
      </div>
      </div>
    </div>
  );
}
